import { Popover } from '@headlessui/react';
import { ChevronDown, LayoutGrid, Pin, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { SECTIONS, TOOL_SECTIONS, type NavSection } from '../../config/navigation';
import { sectionOn } from '../../config/navigationModules';
import { useMemberScope } from '../../hooks/useMemberScope';
import { useCanOpen } from '../../hooks/useMyAccess';
import { useSwitchedOffModules } from '../../hooks/useSwitchedOffModules';

/** The tile of each tool, the mock-up's tints. */
const TONE: Partial<Record<NavSection, string>> = {
  relations: 'bg-blue-50 text-blue-600',
  crm: 'bg-sky-100 text-sky-700',
  inbox: 'bg-amber-50 text-amber-700',
  onboarding: 'bg-green-50 text-green-700',
  contenu: 'bg-otto-100 text-violet-600',
  newsletter: 'bg-rose-50 text-rose-700',
  produit: 'bg-teal-50 text-teal-700',
  autonomie: 'bg-otto-50 text-otto-500',
  dashboard: 'bg-desk-subtle text-desk-strong',
  notes: 'bg-yellow-100 text-yellow-700',
};

/**
 * « Outils » (mock-up « Navigation »): CRM, Contenu, Produit… stop being tabs and open from one
 * menu, two columns, each tool with what it is for; the pin keeps a tool in the header (Relations
 * by default). Réglages close the menu. The active tool is marked.
 */
export function ToolsMenu({
  active,
  pinned,
  onTogglePin,
  counts = {},
}: {
  active: NavSection | null;
  pinned: readonly NavSection[];
  onTogglePin: (tool: NavSection) => void;
  /** What a tool holds for Gilles (Relations: mails to answer), shown even when it is not pinned. */
  counts?: Partial<Record<NavSection, number>>;
}) {
  // A module switched off takes its tools away (PLAN-GENERICITE § 3.7).
  const off = useSwitchedOffModules();
  const scope = useMemberScope();
  const canOpen = useCanOpen();
  const tools = TOOL_SECTIONS.filter((id) => sectionOn(id, off, scope, canOpen)).map((id) => SECTIONS.find((s) => s.id === id)).filter((s) => s !== undefined);
  // A count of a tool that is not in the header would be invisible: the menu's button carries a dot.
  const hidden = tools.some((t) => (counts[t.id] ?? 0) > 0 && !pinned.includes(t.id) && active !== t.id);
  return (
    <Popover className="relative">
      <Popover.Button
        className={({ open }) =>
          twMerge(
            'ml-1.5 flex h-[34px] items-center gap-[7px] rounded-[9px] pl-[11px] pr-2.5 text-[13.5px] font-semibold text-desk-strong ring-1 ring-inset ring-desk-ring transition hover:bg-desk-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
            open && 'bg-desk-canvas',
          )
        }
      >
        <LayoutGrid size={15} strokeWidth={1.75} aria-hidden="true" />
        Outils
        {hidden && <span className="h-2 w-2 rounded-full bg-brand-500" aria-label="Un outil a du nouveau" />}
        <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
      </Popover.Button>
      <Popover.Panel className="absolute left-0 z-40 mt-2 w-[620px] max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-2.5 shadow-[0_24px_64px_-12px_rgba(16,24,40,0.28)] ring-1 ring-desk-ring">
        {({ close }) => (
          <>
            <ul className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
              {tools.map((tool) => {
                const Icon = tool.icon;
                const isPinned = pinned.includes(tool.id);
                return (
                  <li
                    key={tool.id}
                    className={twMerge('flex items-center rounded-[11px] pr-1.5 hover:bg-desk-canvas', active === tool.id && 'bg-desk-canvas')}
                  >
                    <Link
                      to={tool.href}
                      onClick={() => close()}
                      aria-current={active === tool.id ? 'page' : undefined}
                      className="grid min-w-0 flex-1 grid-cols-[34px_minmax(0,1fr)] items-center gap-3 rounded-[11px] p-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                    >
                      <span className={twMerge('flex h-[34px] w-[34px] items-center justify-center rounded-[10px]', TONE[tool.id] ?? TONE.dashboard)}>
                        <Icon className="h-[17px] w-[17px]" />
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 text-[13.5px] font-semibold text-desk-ink">
                          {tool.label}
                          {(counts[tool.id] ?? 0) > 0 && (
                            <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-500 px-[5px] text-[11px] font-bold text-white">
                              {(counts[tool.id] ?? 0) > 99 ? '99+' : counts[tool.id]}
                            </span>
                          )}
                        </span>
                        {tool.description && <span className="block truncate text-[12.5px] text-desk-muted">{tool.description}</span>}
                      </span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => onTogglePin(tool.id)}
                      aria-pressed={isPinned}
                      aria-label={isPinned ? `Désépingler ${tool.label}` : `Épingler ${tool.label} dans le bandeau`}
                      title={isPinned ? 'Épinglé dans le bandeau (cliquer pour retirer)' : 'Épingler dans le bandeau'}
                      className={twMerge(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                        isPinned ? 'text-brand-500' : 'text-desk-faint hover:text-desk-strong',
                      )}
                    >
                      <Pin size={15} strokeWidth={1.75} fill={isPinned ? 'currentColor' : 'none'} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-desk-line px-2.5 pb-0.5 pt-2.5 text-[12.5px] text-desk-muted">
              <span>Épingle un outil pour le garder dans le bandeau.</span>
              {canOpen('/admin/settings') && (
                <Link
                  to="/admin/settings"
                  onClick={() => close()}
                  className="ml-auto flex items-center gap-1.5 rounded-md px-1.5 py-1 font-medium text-desk-strong hover:bg-desk-canvas"
                >
                  <Settings size={14} strokeWidth={1.75} aria-hidden="true" />
                  Réglages
                </Link>
              )}
            </div>
          </>
        )}
      </Popover.Panel>
    </Popover>
  );
}
